import { FC, MutableRefObject, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { skipToken } from '@reduxjs/toolkit/query';

import { putUtkast } from 'store/utkastSlice';

import { useAppDispatch } from 'hooks/defaultStoreHooks';

import { RaaExpandablePreview } from 'raa-react';

import {
  DrawerBody,
  DrawerContainer,
  DrawerFooter,
  DrawerSection,
  NavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import ScrollToError from 'components/fieldComponents/scrollToError/ScrollToError';
import {
  OmradeFormSchema,
  OmradeFormValues,
  PubliceraOmradeFormSchema
} from 'components/utkast/utkastRegistration/UtkastFormSchema';
import BeskrivningSection from 'components/utkast/utkastRegistration/sections/BeskrivningSection';
import FastighetsinformationSection from 'components/utkast/utkastRegistration/sections/FastighetsinformationSection';
import FotografiSection from 'components/utkast/utkastRegistration/sections/FotografiSection';
import GrundinformationSection from 'components/utkast/utkastRegistration/sections/GrundinformationSection';
import HistorikSection from 'components/utkast/utkastRegistration/sections/HistorikSection';
import ReferenserSection from 'components/utkast/utkastRegistration/sections/ReferenserSection';
import SkyddSection from 'components/utkast/utkastRegistration/sections/SkyddSection';
import TillkomstSection from 'components/utkast/utkastRegistration/sections/TillkomstSection';
import UtkastActionsSection from 'components/utkast/utkastRegistration/sections/UtkastActionsSection';

import {
  BebyggelseobjektDomaner,
  BebyggelseobjektUtkast,
  BebyggelseobjektUtkastOmradePut,
  HandelseUtkast,
  Upphovsmaninsats,
  Upphovsmanroll,
  VinjettPutParam
} from 'models';
import { RequestStatus, RequestType } from 'models/appModels/Request';

import { useGetDokumentationQuery, usePutVinjettMutation } from 'api/endpoints/dokumentationSlice';
import { useBlockerContext } from 'context/navigationBlockContext';
import { Form, Formik, FormikHelpers } from 'formik';
import { andamalToAndamalPut } from 'utilities/convertUtkastTypes';
import { setApiValidationErrors } from 'utilities/formUtilities';
import { AnyObject } from 'yup';

interface OmradeUtkastFormProps {
  utkastData: BebyggelseobjektUtkast;
  domaner: BebyggelseobjektDomaner;
  requestStatus: RequestStatus;
  requestType: RequestType;
  deletedOrPublishedRef: MutableRefObject<boolean>;
}

const OmradeUtkastForm: FC<OmradeUtkastFormProps> = ({
  utkastData,
  domaner,
  requestStatus,
  requestType,
  deletedOrPublishedRef
}) => {
  const [initialValues, setInitialValues] = useState<OmradeFormValues>();
  const { id } = useParams();
  const dispatch = useAppDispatch();

  const [dirtyForm, setDirtyForm] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formValidationSchema, setFormValidationSchema] = useState<AnyObject>(OmradeFormSchema);
  const [selectedVinjett, setSelectedVinjett] = useState<VinjettPutParam>();

  const { setNavigationBlocker } = useBlockerContext();

  const { data: dokumentation, isFetching: getDokumentationIsFetching } = useGetDokumentationQuery(id ?? skipToken);

  const [putUtkastVinjett] = usePutVinjettMutation();

  const setInitialFormValues = (utkastData: BebyggelseobjektUtkast): void => {
    const formValues: OmradeFormValues = {
      anteckning: utkastData.anteckning || '',
      syfte: '',
      karaktarsdrag: utkastData.karaktarsdrag || '',
      namn: utkastData.namn || '',
      alternativnamn: [],
      ursprungligt_primar_andamal: utkastData.ursprungligt_primar_andamal
        ? andamalToAndamalPut(utkastData.ursprungligt_primar_andamal)
        : null,
      ursprungligt_sekundar_andamal: utkastData.ursprungligt_sekundar_andamal
        ? utkastData.ursprungligt_sekundar_andamal.map(andamalToAndamalPut)
        : [],
      skydd: {
        motivering: utkastData.skydd.motivering
      },
      beskrivning: utkastData.beskrivning || '',
      historik: utkastData.historik || '',
      geometri: utkastData.geometri,
      lokal_klassificering: utkastData.lokal_klassificering || '',
      referenser: utkastData.referenser || '',
      handelser: utkastData.handelser?.length
        ? utkastData.handelser.map((handelse: HandelseUtkast) => ({
            attribut_id: handelse.attribut_id,
            handelsetyp: handelse.handelsetyp,
            kommentar: handelse.kommentar,
            datering_kand: String(handelse.datering_kand),
            datering_from: handelse.datering_from ? String(handelse.datering_from) : '',
            datering_tom: handelse.datering_tom ? String(handelse.datering_tom) : '',
            upphovsman: handelse.upphovsmanroller.map((upphovsmaninsats: Upphovsmaninsats) => ({
              id: upphovsmaninsats.upphovsman.upphovsman_id,
              roller: upphovsmaninsats.roller.map((upphovsmanroll: Upphovsmanroll) => upphovsmanroll.rolltyp)
            }))
          }))
        : []
    };
    setInitialValues(formValues);
  };

  useEffect(() => {
    if (utkastData && domaner) {
      setInitialFormValues(utkastData);
    }
  }, [utkastData, domaner]);

  useEffect(() => {
    setNavigationBlocker({
      message: 'Formuläret har ändringar som inte har sparats. Vill du fortsätta utan att spara?',
      blocked: !submitting && dirtyForm,
      cancelText: 'avbryt',
      proceedText: 'fortsätt'
    });
  }, [dirtyForm, setNavigationBlocker, submitting]);

  const onSelectVinjett = async (utkastId: string, vinjettParam: VinjettPutParam) => {
    setSelectedVinjett(vinjettParam);
    await putUtkastVinjett({ utkastId, vinjettParam });
  };

  const handleSubmit = async (
    values: OmradeFormValues,
    { setFieldError, setSubmitting }: FormikHelpers<OmradeFormValues>
  ): Promise<void> => {
    if (!id) {
      throw new Error('Fick inget id för utkast!');
    }

    const omradeUtkastPut: BebyggelseobjektUtkastOmradePut = {
      beskrivning: values.beskrivning,
      anteckning: values.anteckning || '',
      historik: values.historik,
      karaktarsdrag: values.karaktarsdrag || '',
      handelser: values.handelser.map((formValue) => ({
        attribut_id: formValue.attribut_id,
        handelsetyp: formValue.handelsetyp,
        datering_from: formValue.datering_from ? Number(formValue.datering_from) : null,
        datering_tom: formValue.datering_tom ? Number(formValue.datering_tom) : null,
        datering_kand: formValue.datering_kand === 'true',
        kommentar: formValue.kommentar,
        upphovsman: formValue.upphovsman
      })),
      namn: values.namn || '',
      skydd: {
        motivering: values.skydd.motivering
      },
      ursprungligt_primar_andamal: values.ursprungligt_primar_andamal,
      ursprungligt_sekundar_andamal: values.ursprungligt_sekundar_andamal,
      geometri: values.geometri,
      referenser: values.referenser,
      lokal_klassificering: values.lokal_klassificering
    };

    try {
      await dispatch(putUtkast({ utkastId: id, utkast: omradeUtkastPut })).unwrap();
    } catch (error: any) {
      setApiValidationErrors(error, setFieldError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {initialValues && (
        <Formik
          initialValues={initialValues}
          validationSchema={formValidationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          innerRef={(ref) => {
            setSubmitting(ref?.isSubmitting || false);
            setDirtyForm(ref?.dirty || false);
          }}
        >
          {({ dirty }) => (
            <Form id="utkastForm" className="tw-h-full">
              <DrawerContainer>
                <NavigationDrawerHead />
                <DrawerBody>
                  <FastighetsinformationSection utkastData={utkastData} dirty={dirty} />
                  <GrundinformationSection
                    primarKategoriLabel="Områdeskategori *"
                    sekundarKategoriLabel="Ytterligare kategori"
                    utkastData={utkastData}
                    domaner={domaner}
                  />
                  <TillkomstSection />
                  <SkyddSection utkastData={utkastData} domaner={domaner} />
                  <BeskrivningSection utkastData={utkastData} domaner={domaner} showEgenskaper={false} />
                  <HistorikSection />
                  <FotografiSection
                    utkast={utkastData}
                    dokumentation={dokumentation ?? []}
                    getDokumentationIsFetching={getDokumentationIsFetching}
                    selectedVinjett={selectedVinjett}
                    onSelectVinjett={onSelectVinjett}
                  />
                  <ReferenserSection />
                  <BerordaFastigheterSection utkastData={utkastData} />
                </DrawerBody>
                <DrawerFooter>
                  <UtkastActionsSection
                    utkastData={utkastData}
                    requestStatus={requestStatus}
                    requestType={requestType}
                    setFormValidationSchema={setFormValidationSchema}
                    sparaFormSchema={OmradeFormSchema}
                    publiceraFormSchema={PubliceraOmradeFormSchema}
                    deletedOrPublishedRef={deletedOrPublishedRef}
                  />
                </DrawerFooter>
              </DrawerContainer>
              <ScrollToError />
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

const BerordaFastigheterSection: FC<{ utkastData: BebyggelseobjektUtkast }> = ({ utkastData }) => {
  return (
    <DrawerSection>
      <fieldset>
        <legend className="tw-heading-md tw-mb-4">Berörda fastigheter</legend>
        {utkastData.berorda_fastigheter && (
          <RaaExpandablePreview height={80} ariaLabel="Lista med berörda fastigheter">
            <ul className="tw-mt-2">
              {utkastData.berorda_fastigheter.map((fast, index) => {
                return (
                  <li key={fast + index} className="tw-capitalize">
                    {fast.toLowerCase()}
                  </li>
                );
              })}
            </ul>
          </RaaExpandablePreview>
        )}
      </fieldset>
    </DrawerSection>
  );
};

export default OmradeUtkastForm;
